import { effect, inject, PLATFORM_ID, untracked } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStore,
  withHooks,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import {
  catchError,
  forkJoin,
  map,
  of,
  pipe,
  Subject,
  switchMap,
  takeUntil,
} from 'rxjs';
import { isBrowserPlatform } from '@core/state/signal-store';
import { EthereumSocketService } from '@core/services/ethereum-socket.service';
import {
  readLaunchpadProjectId,
  watchLaunchpadProjectId,
} from '@shared/utils/route-param.util';
import type {
  InvestorContext,
  LaunchpadOffering,
} from '../models/launchpad.types';
import { LaunchpadApiFacade } from '../services/launchpad-api.facade';

interface OfferingContextState {
  projectId: number;
  offering: LaunchpadOffering | null;
  investorContext: InvestorContext | null;
  loading: boolean;
  error: string;
}

const idleState = {
  offering: null as LaunchpadOffering | null,
  investorContext: null as InvestorContext | null,
  loading: false,
  error: '',
};

const initialState: OfferingContextState = {
  projectId: 0,
  ...idleState,
};

export const OfferingContextStore = signalStore(
  withState(initialState),
  withMethods((store) => {
    const api = inject(LaunchpadApiFacade);
    const platformId = inject(PLATFORM_ID);
    const ethSocket = inject(EthereumSocketService);
    const destroy$ = new Subject<void>();

    const loadOfferingContext = rxMethod<number>(
      pipe(
        switchMap((projectId) => {
          if (!projectId || !isBrowserPlatform(platformId)) {
            patchState(store, { projectId, ...idleState });
            return of(null);
          }
          const sameProject = store.projectId() === projectId && !!store.offering();
          patchState(store, {
            projectId,
            loading: true,
            error: '',
            // Keep prior offering during refresh so invest page does not flash
            // "Offering unavailable" (e.g. wallet chainChanged remount/reload).
            ...(sameProject
              ? {}
              : { offering: null, investorContext: null }),
          });
          return api.getOffering(projectId).pipe(
            switchMap((offering) =>
              forkJoin({
                offering: of(offering),
                investorContext: api.getInvestorContext(
                  offering.fundraiseStatus,
                ),
              }),
            ),
            map(({ offering, investorContext }) => ({
              offering,
              investorContext,
              loading: false,
              error: '',
            })),
            catchError(() =>
              of({
                offering: sameProject ? store.offering() : null,
                investorContext: sameProject ? store.investorContext() : null,
                loading: false,
                error: 'Failed to load offering details.',
              }),
            ),
          );
        }),
        tapResponse({
          next: (result) => {
            if (!result) {
              return;
            }
            patchState(store, result);

            const saleId = result.offering?.idoSale?.idoSaleId;
            if (saleId) {
              ethSocket
                .onIdoBought()
                .pipe(takeUntil(destroy$))
                .subscribe((evt) => {
                  if (evt.idoSaleId !== saleId) return;
                  const current = store.offering();
                  if (!current?.idoSale) return;
                  const issued = evt.issued ?? current.idoSale.issued;
                  const issuedNum = Number(issued);
                  patchState(store, {
                    offering: {
                      ...current,
                      totalRaised: Number.isFinite(issuedNum)
                        ? issuedNum / 1e18
                        : current.totalRaised,
                      idoSale: { ...current.idoSale, issued },
                    },
                  });
                });
            }
          },
          error: () =>
            patchState(store, {
              offering: null,
              investorContext: null,
              loading: false,
              error: 'Failed to load offering details.',
            }),
        }),
      ),
    );

    return {
      load(projectId: number): void {
        destroy$.next();
        loadOfferingContext(projectId);
      },
      dispose(): void {
        destroy$.next();
        destroy$.complete();
      },
    };
  }),
  withHooks((store) => {
    const route = inject(ActivatedRoute);
    const router = inject(Router);
    const platformId = inject(PLATFORM_ID);

    return {
      onInit() {
        const initialProjectId = readLaunchpadProjectId(route, router);
        if (initialProjectId && isBrowserPlatform(platformId)) {
          patchState(store, {
            projectId: initialProjectId,
            loading: true,
            error: '',
          });
        }

        const projectId = toSignal(watchLaunchpadProjectId(route, router), {
          initialValue: initialProjectId,
        });

        effect(() => {
          const id = projectId();
          if (!id) {
            return;
          }
          untracked(() => store.load(id));
        });
      },
      onDestroy() {
        store.dispose();
      },
    };
  }),
);
